import { ReactNode } from 'react';
import styled from 'styled-components';

import { CellContext, ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Dimensions, Metrics } from 'components/insights/constants';
import { formatDaysValue, formatPercentage, formatResponseTime } from 'components/insights/formatting';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { colors } from 'styles/theme/colors';

import { findRollupByGroupingValue, groupingValueFor, totalDimsFor } from '../../shared/slideout/rollupHelpers';
import { getReferralCountLabel } from '../constants';

export type ReferralTableRow = {
  entityName: string;
  entityId: string;
  referrals: number;
  uniquePatients: number;
  acceptedRate: number | null;
  deniedRate: number | null;
  canceledRate: number | null;
  pendingRate: number | null;
  avgResponseTime: number | null;
  avgCreatedToDischarge: number | null;
  avgDaysToStartCare: number | null;
  avgAcceptedToDischarge: number | null;
  isSubRow?: boolean;
  parentId?: string;
  subRows?: ReferralTableRow[];
};

export enum ReferralColumnKey {
  ENTITY = 'entityName',
  REFERRALS = 'referrals',
  UNIQUE_PATIENTS = 'uniquePatients',
  ACCEPTED_RATE = 'acceptedRate',
  DENIED_RATE = 'deniedRate',
  CANCELED_RATE = 'canceledRate',
  PENDING_RATE = 'pendingRate',
  AVG_RESPONSE_TIME = 'avgResponseTime',
  AVG_CREATED_TO_DISCHARGE = 'avgCreatedToDischarge',
  AVG_DAYS_TO_START_CARE = 'avgDaysToStartCare',
  AVG_ACCEPTED_TO_DISCHARGE = 'avgAcceptedToDischarge',
}

export const BASE_REFERRAL_COLUMNS: readonly ReferralColumnKey[] = [
  ReferralColumnKey.ENTITY,
  ReferralColumnKey.REFERRALS,
  ReferralColumnKey.UNIQUE_PATIENTS,
  ReferralColumnKey.ACCEPTED_RATE,
  ReferralColumnKey.DENIED_RATE,
  ReferralColumnKey.CANCELED_RATE,
  ReferralColumnKey.PENDING_RATE,
  ReferralColumnKey.AVG_RESPONSE_TIME,
  ReferralColumnKey.AVG_CREATED_TO_DISCHARGE,
];

const CELL_PADDING = { meta: { padding: '16px 12px' } };
const FIRST_CELL_PADDING = { meta: { padding: '16px 12px 16px 24px' } };
const STRETCH_CELL = { meta: { padding: '16px 12px', style: { alignSelf: 'stretch' } } };

export type EntityCellRenderer = (info: CellContext<ReferralTableRow, string>) => ReactNode;

const defaultEntityCell: EntityCellRenderer = (info) => <EntityName>{info.getValue()}</EntityName>;

// HEALTH_PLAN_CLASSIFICATION's "None" bucket — referrals with no plan — displays as "Other" in
// top-level table rows and top-plan tiles. PLAN_TYPE_CLASSIFICATION (only ever a sub-row) keeps "None".
const NONE_ENTITY_DISPLAY_NAMES: Partial<Record<Dimensions, string>> = {
  [Dimensions.HEALTH_PLAN_CLASSIFICATION]: 'Other',
};

// Top-level rows: only the entity (name + id) preserved, everything else rolled up.
export function buildReferralRows(
  rollups: AnalyticsResponseRollupCollection,
  dimension?: Dimensions
): ReferralTableRow[] {
  const target = groupingValueFor(totalDimsFor(rollups), new Set([0, 1]));
  const rollup = findRollupByGroupingValue(rollups, target);
  if (!rollup) return [];
  return readRollupRows(rollup, 0, dimension ? NONE_ENTITY_DISPLAY_NAMES[dimension] : undefined);
}

// Sub-divisible slideout query is `[entity:with_id, subEntity:with_id, status, ...]`. Top rows
// preserve only the entity (name + id); sub-rows preserve entity + subEntity.
export function buildReferralRowsWithSubRows(
  rollups: AnalyticsResponseRollupCollection,
  dimension?: Dimensions
): ReferralTableRow[] {
  const totalDims = totalDimsFor(rollups);

  const topTarget = groupingValueFor(totalDims, new Set([0, 1]));
  const topRollup = findRollupByGroupingValue(rollups, topTarget);
  const topNoneName = dimension ? NONE_ENTITY_DISPLAY_NAMES[dimension] : undefined;
  const topRows = topRollup ? readRollupRows(topRollup, 0, topNoneName) : [];

  const subTarget = groupingValueFor(totalDims, new Set([0, 1, 2, 3]));
  const rollup = findRollupByGroupingValue(rollups, subTarget);
  if (!rollup) return topRows;

  const parentIds = rollup.dimensions.getCurrentDimensionValuesByIndex(1);
  const allSubRows = readRollupRows(rollup, 2).map((row, i) => ({
    ...row,
    isSubRow: true as const,
    parentId: parentIds[i] ?? '',
  }));

  const byParent = new Map<string, ReferralTableRow[]>();
  for (const sub of allSubRows) {
    if (!sub.parentId) continue;
    const list = byParent.get(sub.parentId);
    if (list) list.push(sub);
    else byParent.set(sub.parentId, [sub]);
  }

  return topRows.map((row) => {
    const children = row.entityId ? byParent.get(row.entityId) : undefined;
    return children?.length ? { ...row, subRows: children } : row;
  });
}

function readRollupRows(
  rollup: AnalyticsResponseRollupCollection['groupings'][number],
  nameDimensionIndex: number,
  noneDisplayName?: string
): ReferralTableRow[] {
  const entityNames = rollup.dimensions.getCurrentDimensionValuesByIndex(nameDimensionIndex);
  const entityIds = rollup.dimensions.getCurrentDimensionValuesByIndex(nameDimensionIndex + 1);
  const referralCounts = rollup.metrics.getCurrentSeries(Metrics.REVIEW_ID_COUNT);
  const uniquePatientCounts = rollup.metrics.getCurrentSeries(Metrics.LOCATION_EPISODE_ID_COUNT);
  const acceptanceRates = rollup.metrics.getCurrentSeries(Metrics.ACCEPTANCE_RATE);
  const denialRates = rollup.metrics.getCurrentSeries(Metrics.DENIAL_RATE);
  const cancelRates = rollup.metrics.getCurrentSeries(Metrics.CANCEL_RATE);
  const pendingRates = rollup.metrics.getCurrentSeries(Metrics.PENDING_RATE);
  const responseTimes = rollup.metrics.getCurrentSeries(Metrics.RESPONSE_TIME_SECONDS_AVERAGE);
  const createdToDischargeDays = rollup.metrics.getCurrentSeries(Metrics.CREATED_TO_DISCHARGE_DAYS_AVERAGE);
  const daysToStartCare = rollup.metrics.getCurrentSeries(Metrics.DAYS_TO_START_CARE_AVERAGE);
  const acceptedToDischargeDays = rollup.metrics.getCurrentSeries(Metrics.ACCEPTED_TO_DISCHARGE_DAYS_AVERAGE);

  return entityNames.map((entityName, i) => ({
    entityName: noneDisplayName && entityName === 'None' ? noneDisplayName : entityName,
    entityId: entityIds[i] ?? '',
    referrals: referralCounts[i] ?? 0,
    uniquePatients: uniquePatientCounts[i] ?? 0,
    acceptedRate: acceptanceRates[i] ?? null,
    deniedRate: denialRates[i] ?? null,
    canceledRate: cancelRates[i] ?? null,
    pendingRate: pendingRates[i] ?? null,
    avgResponseTime: responseTimes[i] ?? null,
    avgCreatedToDischarge: createdToDischargeDays[i] ?? null,
    avgDaysToStartCare: daysToStartCare[i] ?? null,
    avgAcceptedToDischarge: acceptedToDischargeDays[i] ?? null,
  }));
}

type BuildColumnsOpts = {
  dimension: Dimensions;
  entityHeader: string;
  entityCell?: EntityCellRenderer;
  visible?: readonly ReferralColumnKey[];
  referralsHeader?: string;
};

export function buildReferralColumns({
  dimension,
  entityHeader,
  entityCell = defaultEntityCell,
  visible = BASE_REFERRAL_COLUMNS,
  referralsHeader,
}: BuildColumnsOpts): ColumnDef<ReferralTableRow, any>[] {
  const helper = createColumnHelper<ReferralTableRow>();
  const visibleSet = new Set(visible);

  const all: Array<{ key: ReferralColumnKey; def: ColumnDef<ReferralTableRow, any> }> = [
    {
      key: ReferralColumnKey.ENTITY,
      def: helper.accessor('entityName', {
        header: entityHeader,
        cell: entityCell,
        size: 320,
        enableSorting: true,
        ...FIRST_CELL_PADDING,
      }),
    },
    {
      key: ReferralColumnKey.REFERRALS,
      def: helper.accessor('referrals', {
        header: referralsHeader ?? getReferralCountLabel(dimension),
        cell: (info) => info.getValue(),
        size: 160,
        enableSorting: true,
        ...FIRST_CELL_PADDING,
      }),
    },
    {
      key: ReferralColumnKey.UNIQUE_PATIENTS,
      def: helper.accessor('uniquePatients', {
        header: 'Unique Patients',
        cell: (info) => info.getValue(),
        size: 130,
        enableSorting: true,
        ...CELL_PADDING,
      }),
    },
    {
      key: ReferralColumnKey.ACCEPTED_RATE,
      def: helper.accessor('acceptedRate', {
        header: 'Acceptance Rate (%)',
        cell: (info) => <GreenText>{formatPercentage(info.getValue())}</GreenText>,
        size: 155,
        enableSorting: true,
        ...STRETCH_CELL,
      }),
    },
    {
      key: ReferralColumnKey.DENIED_RATE,
      def: helper.accessor('deniedRate', {
        header: 'Denial Rate (%)',
        cell: (info) => <RedText>{formatPercentage(info.getValue())}</RedText>,
        size: 130,
        enableSorting: true,
        ...STRETCH_CELL,
      }),
    },
    {
      key: ReferralColumnKey.CANCELED_RATE,
      def: helper.accessor('canceledRate', {
        header: 'Cancel Rate (%)',
        cell: (info) => formatPercentage(info.getValue()),
        size: 130,
        enableSorting: true,
        ...CELL_PADDING,
      }),
    },
    {
      key: ReferralColumnKey.PENDING_RATE,
      def: helper.accessor('pendingRate', {
        header: 'Pending Rate (%)',
        cell: (info) => formatPercentage(info.getValue()),
        size: 135,
        enableSorting: true,
        ...CELL_PADDING,
      }),
    },
    {
      key: ReferralColumnKey.AVG_RESPONSE_TIME,
      def: helper.accessor('avgResponseTime', {
        header: 'Avg. Response Time',
        cell: (info) => formatResponseTime(info.getValue()),
        size: 155,
        enableSorting: true,
        ...CELL_PADDING,
      }),
    },
    {
      key: ReferralColumnKey.AVG_CREATED_TO_DISCHARGE,
      def: helper.accessor('avgCreatedToDischarge', {
        header: 'Avg. Referral Created to Discharge',
        cell: (info) => formatDaysValue(info.getValue()),
        size: 235,
        enableSorting: true,
        ...CELL_PADDING,
      }),
    },
    {
      key: ReferralColumnKey.AVG_DAYS_TO_START_CARE,
      def: helper.accessor('avgDaysToStartCare', {
        header: 'Avg. Days to Start Care',
        cell: (info) => formatDaysValue(info.getValue()),
        size: 170,
        enableSorting: true,
        ...CELL_PADDING,
      }),
    },
    {
      key: ReferralColumnKey.AVG_ACCEPTED_TO_DISCHARGE,
      def: helper.accessor('avgAcceptedToDischarge', {
        header: 'Avg. Accepted to Discharge',
        cell: (info) => formatDaysValue(info.getValue()),
        size: 195,
        enableSorting: true,
        ...CELL_PADDING,
      }),
    },
  ];

  return all.filter(({ key }) => visibleSet.has(key)).map(({ def }) => def);
}

export const EntityName = styled.span`
  color: ${({ theme }) => theme.colors.primaryBlue};
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.18px;
`;

export const EntityButton = styled.button`
  background: none;
  border: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  display: block;
  width: calc(100% + 36px);
  margin: -16px -12px -16px -24px;
  padding: 16px 12px 16px 24px;
  color: ${({ theme }) => theme.colors.primaryBlue};
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.18px;
  white-space: normal;
  overflow-wrap: anywhere;

  td:hover & {
    text-decoration: underline;
  }
`;

const GreenText = styled.span`
  color: ${({ theme }) => theme.colors.accentGreen};
  background-color: ${colors.accentGreen05};
  font-weight: var(--font-weight-bold);
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: -16px -12px;
  padding: 16px 12px;
  flex: 1;
`;

const RedText = styled.span`
  color: ${({ theme }) => theme.colors.accentRed};
  background-color: ${colors.accentRed05};
  font-weight: var(--font-weight-bold);
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: -16px -12px;
  padding: 16px 12px;
  flex: 1;
  width: 100%;
`;
